import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

type AuthLayoutVariant = "church" | "platform";

interface AuthLayoutProps {
  variant: AuthLayoutVariant;
  workspaceLabel: string;
  brandIcon: LucideIcon;
  cardIcon: LucideIcon;
  eyebrow: string;
  heroTitle: string;
  heroDescription: string;
  formTitle: string;
  formDescription: string;
  footer: string;
  children: ReactNode;
}

const variantStyles = {
  church: {
    eyebrow: "text-emerald-300",
    cardIconBackground: "bg-emerald-50",
    cardIcon: "text-emerald-600",
    workspaceLabel:
      "border-emerald-400/20 bg-emerald-500/10 text-emerald-200",
    gradient:
      "bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.40),_transparent_42%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.20),_transparent_38%)]",
  },
  platform: {
    eyebrow: "text-indigo-300",
    cardIconBackground: "bg-indigo-50",
    cardIcon: "text-indigo-600",
    workspaceLabel:
      "border-indigo-400/20 bg-indigo-500/10 text-indigo-200",
    gradient:
      "bg-[radial-gradient(circle_at_top_left,_rgba(79,70,229,0.45),_transparent_42%),radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.20),_transparent_38%)]",
  },
} satisfies Record<
  AuthLayoutVariant,
  {
    eyebrow: string;
    cardIconBackground: string;
    cardIcon: string;
    workspaceLabel: string;
    gradient: string;
  }
>;

export function AuthLayout({
  variant,
  workspaceLabel,
  cardIcon: CardIcon,
  eyebrow,
  heroTitle,
  heroDescription,
  formTitle,
  formDescription,
  footer,
  children,
}: AuthLayoutProps) {
  const styles = variantStyles[variant];

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative hidden overflow-hidden border-r border-white/10 lg:flex">
          <div
            className={`absolute inset-0 ${styles.gradient}`}
          />

          <div className="relative flex h-full w-full flex-col p-12 xl:p-16">
            <Brand
              workspaceLabel={workspaceLabel}
              workspaceLabelClassName={styles.workspaceLabel}
              size="large"
            />

            <div className="flex-1 flex flex-col justify-center max-w-xl">
              <p
                className={`text-sm font-semibold uppercase tracking-[0.22em] ${styles.eyebrow}`}
              >
                {eyebrow}
              </p>

              <h1 className="mt-5 text-5xl font-bold leading-[1.05] tracking-tight text-white xl:text-6xl">
                {heroTitle}
              </h1>

              <p className="mt-6 max-w-lg text-lg leading-8 text-slate-200">
                {heroDescription}
              </p>

              <div className="mt-8">
                <span
                  className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${styles.workspaceLabel}`}
                >
                  {workspaceLabel}
                </span>
              </div>
            </div>
          </div>
        </section>

        <main className="flex items-center justify-center px-6 py-12 sm:px-10 lg:px-14">
          <div className="w-full max-w-lg">
            <div className="mb-10 lg:hidden">
              <Brand
                workspaceLabel={workspaceLabel}
                workspaceLabelClassName={styles.workspaceLabel}
                size="compact"
              />
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/95 backdrop-blur-xl p-7 shadow-2xl shadow-black/30 sm:p-9">
              <div>
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-2xl ${styles.cardIconBackground}`}
                >
                  <CardIcon
                    className={`h-6 w-6 ${styles.cardIcon}`}
                  />
                </div>

                <h2 className="mt-6 text-3xl font-bold tracking-tight text-slate-950">
                  {formTitle}
                </h2>

                <p className="mt-2 text-sm leading-6 text-slate-500">
                  {formDescription}
                </p>
              </div>

              {children}
            </div>

            <p className="mt-6 text-center text-xs leading-5 text-slate-500">
              {footer}
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

interface BrandProps {
  workspaceLabel: string;
  workspaceLabelClassName: string;
  size: "large" | "compact";
}

function Brand({
  workspaceLabel,
  workspaceLabelClassName,
  size,
}: BrandProps) {
  const logoClassName =
    size === "large"
      ? "h-auto w-[430px] object-contain xl:w-[470px]"
      : "h-auto w-[280px] object-contain sm:w-[320px]";

  return (
    <div className="flex flex-col items-start">
      <div className="relative">
        <div className="pointer-events-none absolute left-8 top-1/2 h-40 w-40 -translate-y-1/2 rounded-full bg-indigo-500/15 blur-3xl" />

        <img
          src="/official_logo.png"
          alt="MinistryIQ — See More. Care Better. Lead Smarter."
          className={`relative z-10 ${logoClassName}`}
        />
      </div>

      {size === "compact" && (
        <span
          className={`mt-4 inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${workspaceLabelClassName}`}
        >
          {workspaceLabel}
        </span>
      )}
    </div>
  );
}